/**
 * specs/product-kernel/dashboard-ukrainian-localization.md
 *
 * English dictionary — the fallback every `uk.ts` gap resolves to. Flat key→string map,
 * namespaced by tab/component.
 */

export const en: Record<string, string> = {
  // ---- common (reused across many components — one word per meaning) ----
  'common.loading': 'Loading...',
  'common.cancel': 'Cancel',
  'common.submit': 'Submit',
  'common.submitting': 'Submitting...',
  'common.notesOptional': 'Notes (optional)',
  'common.notesRequired': 'Notes (required)',
  'common.notesPlaceholderHelps': 'Why this change — helps the approving operator make an informed call.',
  'common.continue': 'Continue',
  'common.operatorApiKeyPlaceholder': 'Operator API key',
  'common.operatorKeyRequiredTitle': 'Operator key required',
  'common.forgetOperatorKey': 'Forget operator key',
  'common.operatorKeyInvalid': 'That key was not recognized, or has been revoked. Check it and try again.',
  'common.operatorSessionSigningIn': 'Signing in…',
  'blocks.operatorKeyDescription':
    'Creating a manual block records who did it, using a real operator identity — sign in with an operator key to continue.',
  'author.operatorKeyDescription':
    'Recording a rule-soundness review needs a real operator identity — sign in with an operator key to continue.',
  'cases.operatorKeyDescription':
    'Recording a case-review decision needs a real operator identity — sign in with an operator key to continue.',
  'common.notEnoughData': 'Not enough data yet',
  'common.outcome': 'Outcome',
  'common.approve': 'Approve',
  'common.reject': 'Reject',
  'common.proposedBy': 'Proposed By',
  'common.approvedBy': 'Approved By',
  'common.you': 'You',
  'common.notes': 'Notes',
  'common.actions': 'Actions',
  'common.cannotApproveOwnProposal': "You can't approve or reject your own proposal",
  'common.clientOperator': 'client operator',
  'common.wowyrdOperator': 'WoWyrd operator',
  'common.signedInAsPrefix': 'Signed in as',

  // ---- decision labels (displayed text only — underlying data keys stay in English).
  // Deliberately uppercase in English, matching the raw Decision enum text this always
  // rendered before i18n existed (TransactionFeed's filter buttons showed `{d}` directly,
  // e.g. "ALLOW") — several existing tests query this exact casing, and it's also visually
  // distinct from title-case labels elsewhere (StatCard's "Allow"), which matters for
  // getByText uniqueness. ----
  'decision.all': 'ALL',
  'decision.allow': 'ALLOW',
  'decision.review': 'REVIEW',
  'decision.block': 'BLOCK',

  // ---- shell (App.tsx) ----
  'shell.subtitleDefault': 'Pre-transaction risk control',
  'shell.navRisk': 'Risk',
  'shell.live': 'Live',
  'shell.loading': 'Loading...',
  'shell.langToggleLabel': 'Language',

  // ---- header / tab nav (riskDashboard.tsx) ----
  'header.title': 'Risk Fraud Prevention',
  'header.subtitle': 'Observe-only pre-transaction risk evaluation · explainable rule pack',
  // specs/simulation-lab/demo-neobank-traffic.md — real, live-read mode badge, replacing the
  // always-static subtitle above (kept as a fallback while /health hasn't resolved yet).
  'header.modeLabel': 'Mode',
  // Founder (2026-11-02, real demo.wowyrd.net finding): one name per mode everywhere in the
  // dashboard — "Enforce" is the glossary term; this badge used to say "Blocking" for the same
  // mode `enforcementMode.enforce` elsewhere calls "Enforce".
  'header.modeEnforce': 'Enforce',
  'header.modeObserve': 'Observe',
  'header.modeAdvisory': 'Advisory',
  // Founder's own draft revised for accuracy (flagged by the peer session, confirmed with the
  // founder): WoWyrd returns a decision before the payment executes; the client's own system
  // acts on it — not an autonomous payment-rail stop. Also dropped the founder's own "as in
  // this demo" clause from the original draft: this same line renders on every deployment
  // (enforce or observe), not just the demo, and the mode badge right above it already shows
  // which one is active — a demo-specific clause would be wrong on a real client install.
  'header.modesExplainer':
    'WoWyrd runs in two modes: Observe (scores and recommends, blocks nothing) or Enforce (the decision comes back before the payment executes, and your system acts on it to stop risky payments).',
  'header.liveFeed': 'Live feed',
  'header.reconnecting': 'Reconnecting',
  'header.evaluated': 'evaluated',
  'header.blocked': 'blocked',
  'nav.ariaLabel': 'Risk sections',
  'tabs.feed': 'Transaction Feed',
  'tabs.blocks': 'Blocked Identifiers',
  'tabs.velocity': 'Velocity',
  'tabs.reasons': 'Reasons',
  'tabs.coverage': 'Coverage',
  'tabs.cases': 'Cases',
  'tabs.kpis': 'KPIs',
  'tabs.author': 'Author',
  'tabs.calibration': 'Calibration',
  'tabs.actionGates': 'Action Gates',
  'tabs.webhooks': 'Webhooks',
  'tabs.performance': 'Performance',

  // ---- feed (TransactionFeed.tsx) ----
  'feed.statTotal': 'Total',
  'feed.statAllow': 'Allow',
  'feed.statReview': 'Review',
  // Founder decision 2026-10-30 (relayed): "Blocked" regardless of enforcement mode — the
  // count itself is always the real risk verdict (recommended_decision); whether it was
  // actually enforced is disclosed separately via feed.enforcementModeLabel below, not folded
  // into this label. See specs/simulation-lab/demo-would-block-counters-proposal.md.
  'feed.statBlock': 'Blocked',
  'feed.enforcementModeLabel': 'Mode',
  'feed.enforcementModeTooltip':
    'Whether this deployment actually blocks transactions, or only recommends what it would block.',
  'enforcementMode.observe': 'Observe-only — nothing is actually blocked',
  'enforcementMode.advisory': 'Advisory — nothing is actually blocked',
  'enforcementMode.enforce': 'Enforce — approved actions are actually blocked',
  'feed.live': 'Live',
  'feed.reconnecting': 'Reconnecting...',
  'feed.colDecision': 'Decision',
  'feed.colScore': 'Score',
  'feed.colIp': 'IP',
  'feed.colCustomer': 'Customer',
  'feed.colFactors': 'Factors',
  'feed.colLatency': 'Latency',
  'feed.colTime': 'Time',
  'feed.colAmount': 'Amount',
  'feed.colType': 'Type',
  'feed.colCountry': 'Country',
  'feed.colReason': 'Main reason',
  'feed.detailLabel': 'Detail',
  'feed.waitingForTransactions': 'Waiting for transactions...',
  'feed.connectingToFeed': 'Connecting to feed...',
  'feed.showing': 'Showing {{from}}–{{to}} of {{total}}',
  'feed.prev': 'Prev',
  'feed.next': 'Next',
  'feed.wouldBlock': 'would block',
  'feed.wouldBlockTooltip': "Identifier is on the block list — this evaluation only ran under Observe/Advisory mode, so it wasn't hard-blocked",
  'feed.recommendedPrefix': 'recommended: {{decision}}',
  'feed.recommendedTooltip': 'What the rule engine actually determined — this evaluation only ran under Observe/Advisory mode, so the Decision column above always shows ALLOW',
  'feed.blockedFactor': 'blocked',

  // ---- blocks (BlockedIdentifiers.tsx) ----
  'blocks.typeIp': 'IP Address',
  'blocks.typeCustomer': 'Customer ID',
  'blocks.typeDevice': 'Device Fingerprint',
  'blocks.filterAll': 'All ({{total}})',
  'blocks.addBlock': '+ Add Block',
  'blocks.noBlockedIdentifiers': 'No blocked identifiers',
  'blocks.colType': 'Type',
  'blocks.colValue': 'Value',
  'blocks.colReason': 'Reason',
  'blocks.colCreatedBy': 'Created by',
  'blocks.colExpires': 'Expires',
  'blocks.permanent': 'Permanent',
  'blocks.unblock': 'Unblock',
  'blocks.page': 'Page {{page}} of {{totalPages}} ({{total}} total)',
  'blocks.addNewBlock': 'Add New Block',
  'blocks.fieldType': 'Type',
  'blocks.fieldValue': 'Value',
  'blocks.valuePlaceholderIdentifier': 'identifier',
  'blocks.fieldReason': 'Reason',
  'blocks.reasonManualBlock': 'Manual Block',
  'blocks.reasonBankDecline': 'Bank Decline',
  'blocks.reasonChargeback': 'Chargeback',
  'blocks.reasonHighVelocity': 'High Velocity',
  'blocks.reasonVpnTor': 'VPN/Tor',
  'blocks.fieldTtl': 'TTL Hours (0 = permanent)',
  'blocks.blocking': 'Blocking...',
  'blocks.block': 'Block',

  // ---- velocity (VelocityChart.tsx) ----
  'velocity.waitingForTraffic': 'Waiting for traffic data…',
  'velocity.avgReqS': 'Avg Req/s (last 10s)',
  'velocity.blockRate': 'Block Rate (last 10s)',
  'velocity.dataPoints': 'Data Points',
  'velocity.live': 'Live',
  'velocity.paused': 'Paused',
  'velocity.chartTitle': 'Request Velocity (last 60 seconds)',
  'velocity.seriesRequests': 'Requests/s',
  'velocity.seriesBlocked': 'Blocked/s',

  // ---- reasons (ReasonsExplorer.tsx) ----
  'reasons.noRulesTriggered': 'No rules have triggered yet.',
  'reasons.distinctRulesTriggered': 'Distinct rules triggered',
  'reasons.eventsInView': 'Events in view',
  'reasons.feedStatus': 'Feed status',
  'reasons.live': 'Live',
  'reasons.paused': 'Paused',
  'reasons.topTriggeredRules': 'Top Triggered Rules',
  'reasons.aggregatedFrom': 'Aggregated from the {{count}} most recent evaluations currently held in the browser — not a historical query. For a full-period report, use',
  'reasons.colRule': 'Rule',
  'reasons.colReasonCode': 'Reason Code',
  'reasons.colSource': 'Source',
  'reasons.colTriggers': 'Triggers',
  'reasons.colAvgWeight': 'Avg Weight',
  'reasons.colPctOfEvents': '% of Events',

  // ---- coverage (CoveragePanel.tsx) ----
  // specs/simulation-lab/demo-neobank-traffic.md's Coverage panel — every rule in
  // demoRuleCatalog.ts, with a live sighting count tallied off the same WS event stream
  // ReasonsExplorer.tsx already aggregates (never hidden, so a visitor sees the full catalogue).
  'coverage.title': 'Rule-Pack Coverage',
  'coverage.subtitle':
    'Every rule WoWyrd can trigger, and how many times it has fired in the current browser window. A rule with zero sightings is not broken — it just hasn’t fired yet.',
  'coverage.statTotalRules': 'Total rules',
  'coverage.statSeen': 'Seen this window',
  'coverage.statNotYetSeen': 'Not yet seen',
  'coverage.statUnavailable': 'Unavailable',
  'coverage.colRule': 'Rule',
  'coverage.colReasonCode': 'Reason Code',
  'coverage.colStatus': 'Status',
  'coverage.notYetSeen': 'Not yet in this window',
  'coverage.seenCount': 'Seen {{count}} times in this window',

  // ---- cases (CasesQueue.tsx) ----
  'cases.statusTruePositive': 'True positive',
  'cases.statusFalsePositive': 'False positive',
  'cases.statusEscalated': 'Escalated',
  'cases.actionAllow': 'Allow',
  'cases.actionHold': 'Hold',
  'cases.actionStepUp': 'Step up',
  'cases.actionThrottle': 'Throttle',
  'cases.actionDenyCandidate': 'Deny candidate',
  'cases.actionReview': 'Review',
  'cases.noOpenCases': 'No open cases — nothing waiting on review.',
  'cases.noResolvedCases': 'No resolved cases yet.',
  'cases.viewOpen': 'Open',
  'cases.viewResolved': 'Resolved',
  'cases.colDecision': 'Decision',
  'cases.colRecommended': 'Recommended',
  'cases.colScore': 'Score',
  'cases.colCustomer': 'Customer',
  'cases.customerNotProvided': 'no identifier provided',
  'cases.customerRedacted': 'redacted under retention policy',
  'cases.view': 'View',
  'cases.detailTitle': 'Case detail',
  'cases.detailBack': '← Back to queue',
  'cases.detailReasons': 'Reasons',
  'cases.detailReview': 'Analyst review',
  'cases.detailNoReview': 'No review recorded yet.',
  'cases.detailTimeline': 'Account timeline (other flagged events for this account)',
  'cases.detailTimelineEmpty': 'No other flagged events for this account.',
  'cases.exportHtml': 'Export HTML',
  'cases.exportJson': 'Export JSON',
  'cases.exportFailed': 'Export failed — try again.',
  'cases.detailLoadFailed': 'Could not load this case.',
  'cases.colFactors': 'Factors',
  'cases.colEvaluated': 'Evaluated',
  'cases.colReview': 'Review',
  'cases.colActions': 'Actions',
  'cases.block': 'Block',
  'cases.review': 'Review',
  'cases.anonymizedIp': '— (anonymized)',
  'cases.recordReviewDecision': 'Record review decision',
  'cases.escalate': 'Escalate',
  'cases.notesWhyUndocumented': "Why this outcome — an undocumented decision isn't explainable.",
  'cases.submitReview': 'Submit review',
  'cases.customer': 'Customer:',
  'cases.ip': 'IP:',
  'cases.factorsCount': 'Factors ({{count}}):',

  // ---- kpis (ManagerKpisTab.tsx) ----
  'kpis.falsePositiveRate': 'False positive rate',
  'kpis.falsePositiveRateHelp': 'analyst-confirmed, from the Cases queue',
  'kpis.preFlaggedRate': 'Pre-flagged rate',
  'kpis.preFlaggedRateHelp': 'confirmed fraud WoWyrd had already flagged beforehand',
  'kpis.samplesOne': '{{count}} sample',
  'kpis.samplesMany': '{{count}} samples',
  'kpis.decisionVolumeTrend': 'Decision volume trend',
  'kpis.noEvaluationsInWindow': 'No evaluations in this window.',
  'kpis.seriesAllow': 'Allow',
  'kpis.seriesReview': 'Review',
  'kpis.seriesBlock': 'Block',

  // ---- author (AuthorReviewTab.tsx) ----
  'author.description': 'Per-rule soundness — fire rate, ground-truth false-positive rate from analyst-confirmed case reviews, and staleness. Record a periodic review to document oversight.',
  'author.noActiveRules': 'No active rules to review.',
  'author.colRule': 'Rule',
  'author.colFireRate': 'Fire rate',
  'author.colFalsePositiveRate': 'False-positive rate',
  'author.colDaysSinceChange': 'Days since change',
  'author.colLastReview': 'Last review',
  'author.colActions': 'Actions',
  'author.weight': 'weight {{weight}}',
  'author.neverReviewed': 'Never reviewed',
  'author.review': 'Review',
  'author.outcomeSound': 'Sound',
  'author.outcomeNeedsRecalibration': 'Needs recalibration',
  'author.outcomeNeedsRetirement': 'Needs retirement',
  'author.recordSoundnessReview': 'Record soundness review — {{name}}',
  'author.notesWhyUndocumented': "Why this outcome — an undocumented determination isn't a real review.",
  'author.submitReview': 'Submit review',

  // ---- calibration / rules (Calibration.tsx, Rules.tsx) ----
  'calibration.operatorKeyDescription': "Rule calibration is gated by an individually-issued operator key, separate from the dashboard's regular access — issued to WoWyrd engineers and to the client's own operators alike.",
  'rules.title': 'Rules',
  'rules.description': 'Active fraud rules. Proposing a new weight does not change it — a different operator must approve first.',
  'rules.noActiveRules': 'No active rules',
  'rules.colRule': 'Rule',
  'rules.colReasonCode': 'Reason Code',
  'rules.colWeight': 'Weight',
  'rules.colSuggestion': 'Suggestion',
  'rules.colActions': 'Actions',
  'rules.proposeCalibration': 'Propose calibration',
  'rules.proposeCalibrationTitle': 'Propose calibration — {{name}} (currently {{weight}})',
  'rules.newWeightLabel': 'New weight (0–100)',
  'rules.submitProposal': 'Submit proposal',
  'pendingCalibration.title': 'Pending Calibration Proposals',
  'pendingCalibration.description': "Approving applies the weight change and writes a tamper-evident audit row. You cannot approve or reject your own proposal, and a change can only be resolved by another operator from the same organization as the one who proposed it — a client operator's proposal needs a client approver, a WoWyrd operator's needs a WoWyrd approver.",
  'pendingCalibration.noPendingProposals': 'No pending proposals',
  'pendingCalibration.colRule': 'Rule',
  'pendingCalibration.colProposedWeight': 'Proposed Weight',

  // ---- calibration history (CalibrationHistory.tsx) — specs/control-api/
  // rule-weight-calibration.md's "Round 3" (2026-11-01) ----
  'calibrationHistory.title': 'History',
  'calibrationHistory.description': 'Every applied calibration change — a tamper-evident, append-only record. WoWyrd-internal operators only.',
  'calibrationHistory.noHistory': 'No applied changes yet',
  'calibrationHistory.colRule': 'Rule',
  'calibrationHistory.colWeightChange': 'Weight change',
  'calibrationHistory.colApprovedAt': 'Approved at',
  'calibrationHistory.colMode': 'Mode',
  'calibrationHistory.colEffectiveAt': 'Effective at',
  'calibrationHistory.modeSingleOperator': 'Single-operator',
  'calibrationHistory.modeDualControl': 'Dual-control',
  'calibrationHistory.notYetConfirmed': 'Not yet confirmed',
  'calibrationHistory.chainBroken': 'Tamper-evidence check failed — this history may have been altered. Contact an administrator.',
  'calibrationHistory.forbiddenForClientOperator': 'This history view is WoWyrd-internal only. See the evidence report’s own applied-changes section for the client-facing view of the same data.',

  // ---- action gates (ActionGatesTab.tsx, ActionGates.tsx, PendingActionGateProposals.tsx) ----
  'actionGates.operatorKeyDescription': "Action-gate control is gated by an individually-issued operator key, the same one used for rule calibration — issued to WoWyrd engineers and to the client's own operators alike.",
  'actionGates.title': 'Action Gates',
  'actionGates.description': 'Whether a DENY_CANDIDATE recommendation is allowed to become a real, autonomous block — independent of, and in addition to, the enforcement-mode ladder. Proposing a change does not flip it — a different operator must approve first.',
  'actionGates.noGatedActionTypes': 'No gated action types',
  'actionGates.colActionType': 'Action Type',
  'actionGates.colCurrentState': 'Current State',
  'actionGates.colActions': 'Actions',
  'actionGates.denyCandidateToBlock': 'Deny candidate → real block',
  'actionGates.enabled': 'Enabled',
  'actionGates.disabled': 'Disabled',
  'actionGates.proposeChange': 'Propose change',
  'actionGates.proposeChangeTitle': 'Propose change — {{name}} (currently {{state}})',
  'actionGates.requestedState': 'Requested state: {{state}}',
  'actionGates.requestedStateLabel': 'Requested enabled',
  'actionGates.submitProposal': 'Submit proposal',
  'pendingActionGates.title': 'Pending Action Gate Proposals',
  'pendingActionGates.description': "Approving applies the gate change and writes a tamper-evident audit row (its own, independent hash chain — separate from the calibration audit trail). You cannot approve or reject your own proposal, and a change can only be resolved by another operator from the same organization as the one who proposed it.",
  'pendingActionGates.noPendingProposals': 'No pending proposals',
  'pendingActionGates.colActionType': 'Action Type',
  'pendingActionGates.colRequestedState': 'Requested State',
  'pendingActionGates.colApprovalMode': 'Approval',
  'pendingActionGates.singleOperatorMode': 'Single-operator mode',
  'pendingActionGates.dualOperatorMode': 'Needs {{count}} distinct approvers',

  // ---- step-up config (StepUpConfig.tsx) ----
  'stepUp.title': 'Step-Up Configuration',
  'stepUp.description': 'Read-only — the current, approved reason-code mapping overrides and additive score threshold that can yield STEP_UP. Changing either goes through the maker-checker API directly (not from here yet).',
  'stepUp.scoreThreshold': 'Score threshold',
  'stepUp.thresholdSetText': 'Any Review-band score ≥ {{threshold}} yields STEP_UP, regardless of which reason code dominated.',
  'stepUp.thresholdNotSet': 'Not set — this path is off; only the reason-code mapping below (or the hardcoded defaults) apply.',
  'stepUp.noApprovedOverrides': 'No approved reason-code overrides — the hardcoded STEP_UP_REASON_CODES / THROTTLE_REASON_CODES lists apply unchanged.',
  'stepUp.colReasonCode': 'Reason Code',
  'stepUp.colMappedAction': 'Mapped Action',
  'stepUp.colProposedAt': 'Proposed At',

  // ---- webhooks (WebhooksTab.tsx, Webhooks.tsx) ----
  'webhooksTab.operatorKeyDescription': 'Webhook endpoint management is gated by an individually-issued operator key, the same one used for rule calibration and action-gate control.',
  'webhooks.title': 'Webhooks',
  'webhooks.description': 'Client-configured endpoints notified on case-review and action-gate resolution — decision-content-only, PII-free by construction (except reviewed_by, a caller-supplied free-text field). Registering an endpoint takes effect immediately — no maker-checker approval needed for this control, per this spec\'s own FSI review.',
  'webhooks.registerEndpoint': 'Register endpoint',
  'webhooks.eventCaseReviewResolved': 'Case review resolved',
  'webhooks.eventActionApprovalResolved': 'Action approval resolved',
  'webhooks.secretRotated': 'Secret rotated',
  'webhooks.endpointRegistered': 'Endpoint registered',
  'webhooks.secretShownOnceWarning': 'This signing secret is shown only now — it cannot be retrieved again. Store it before closing this panel.',
  'webhooks.done': 'Done',
  'webhooks.noEndpointsRegistered': 'No webhook endpoints registered',
  'webhooks.colEndpoint': 'Endpoint',
  'webhooks.colEvents': 'Events',
  'webhooks.colStatus': 'Status',
  'webhooks.colActions': 'Actions',
  'webhooks.disabledSince': 'Disabled since {{date}}',
  'webhooks.consecutiveFailureOne': '{{count}} consecutive failure',
  'webhooks.consecutiveFailureMany': '{{count}} consecutive failures',
  'webhooks.healthy': 'Healthy',
  'webhooks.hideHistory': 'Hide history',
  'webhooks.history': 'History',
  'webhooks.reenable': 'Re-enable',
  'webhooks.rotateSecret': 'Rotate secret',
  'webhooks.delete': 'Delete',
  'webhooks.loadingDeliveryHistory': 'Loading delivery history...',
  'webhooks.noDeliveriesYet': 'No deliveries yet for this endpoint.',
  'webhooks.colEvent': 'Event',
  'webhooks.colResult': 'Result',
  'webhooks.colAttempts': 'Attempts',
  'webhooks.colWhen': 'When',
  'webhooks.delivered': 'Delivered',
  'webhooks.failed': 'Failed',
  'webhooks.copied': 'Copied',
  'webhooks.copy': 'Copy',
  'webhooks.registerNewEndpoint': 'Register a new webhook endpoint',
  'webhooks.urlLabel': 'URL',
  'webhooks.eventsLabel': 'Events',
  'webhooks.registering': 'Registering...',
  'webhooks.register': 'Register',
  'webhooks.deleteConfirm': 'Delete the webhook endpoint {{url}}? This also removes its delivery history and cannot be undone.',
  'webhooks.rotateConfirm': 'Rotate the signing secret for {{url}}? This takes effect immediately — deliveries will fail signature verification until the receiving side is updated with the new secret.',

  // ---- performance (PerformancePanel.tsx) ----
  'performance.serviceHealth': 'Service Health',
  'performance.updated': 'Updated {{time}}',
  'performance.refresh': 'Refresh',
  'performance.apiServer': 'API Server',
  'performance.ebpfTcProgram': 'eBPF TC Program',
  'performance.redis': 'Redis',
  'performance.postgresql': 'PostgreSQL',
  'performance.wsClients': 'WS Clients:',
  'performance.status': 'Status:',
  'performance.loadingHealthData': 'Loading health data…',
  'performance.healthDataUnavailable': 'Health data unavailable',
  'performance.dashboardConnection': 'Dashboard Connection',
  'performance.webSocketStatus': 'WebSocket Status',
  'performance.connected': 'Connected',
  'performance.reconnectingEllipsis': 'Reconnecting…',
  'performance.reconnectCount': 'Reconnect Count',
  'performance.connectionInterruptions': 'connection interruptions',
  'performance.stable': 'stable',
  'performance.ebpfMapUtilization': 'eBPF Map Utilization',
  'performance.ipBlacklist': 'IP Blacklist',
  'performance.customerBlacklist': 'Customer Blacklist',
  'performance.deviceBlacklist': 'Device Blacklist',
  'performance.kernelCounters': 'Kernel Counters',
  'performance.packetsProcessed': 'Packets Processed',
  'performance.sinceLastRestart': 'total since last restart',
  'performance.packetsDroppedKernel': 'Packets Dropped (kernel)',
  'performance.tcActShotCount': 'TC_ACT_SHOT count',
  'performance.blockRate': 'Block Rate',

  // ---- alert banner (AlertBanner.tsx) ----
  'alert.chargeback': 'Chargeback',
  'alert.fraudBlock': 'Fraud Block',
  'alert.dismiss': 'Dismiss alert',

  // ---- license banner (LicenseBanner.tsx) ----
  'license.unlicensed': 'No license file found',
  'license.invalid': 'License file is invalid',
  'license.expired': 'License expired {{date}}',
  'license.expiringSoon': 'License expires {{date}}',
  'license.active': 'Licensed to {{client}}',
  'license.tierSuffix': ' ({{tier}})',
  'license.offlineMode': 'Offline mode — no outbound threat-intel fetching',
  'license.usageNearLimit': '{{count}} / {{limit}} evaluations this month — approaching your declared volume',
  'license.usageOverLimit': '{{count}} / {{limit}} evaluations this month — over your declared volume',

  // ---- demo provenance banner (DemoProvenanceBanner.tsx) ----
  // specs/simulation-lab/demo-dataset-replay-and-provenance.md
  'demo.provenance.label': 'Demo data',
  'demo.provenance.tooltip':
    'Synthetic traffic shaped from PaySim, created by Edgar Lopez-Rojas, licensed CC BY-SA 4.0 (kaggle.com/datasets/ealaxi/paysim1). No real customers or real transactions.',

  // ---- demo read-only controls (DemoReadOnlyNotice.tsx, Calibration.tsx, ActionGatesTab.tsx,
  // WebhooksTab.tsx, CasesQueue.tsx, BlockedIdentifiers.tsx) ----
  // specs/simulation-lab/demo-neobank-traffic.md — replaces the operator-key-required dead end
  // with a read-only sample preview, and replaces the public demo's write buttons (blocked at
  // the nginx edge already) with a clear message instead of a raw failed-request error.
  'demo.readOnly.badge': 'Read-only in the demo',
  'demo.readOnly.settingsMessage':
    'Read-only in the demo — sample settings shown below, not a real client configuration. Write actions are disabled on the public demo.',
  'demo.readOnly.writeMessage': 'Read-only in the demo — write actions are disabled on the public demo.',
};
